import React, {useCallback} from "react"
import {HeaderComponentProps, PaginatedSelector} from "@/components/paginated-selector/paginated-selector";
import {apiV2} from "@/api/axiosConfig";
import {convertV1ToV2, PaginatedResponse} from "@/types/PaginatedResponse";
import {useAuth} from "@/context/user";
import {ClientFilter} from "@/components/paginated-selector/filters/ClientFilter";
import {LocationTypeFilter} from "@/components/paginated-selector/filters/LocationTypeFilter";
import {FilterQueryParams} from "@/components/paginated-selector/filters/types";
import {DepartmentFilter} from "@/components/paginated-selector/filters/DepartmentFilter";
import {DepartmentName} from "@/lib/departments/DepartmentName";
import {useExportToXlsx} from "@/components/paginated-selector/useExportToXlsx";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";

type SiteBasedAlert = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    departmentID: string;
    locationType: string;
    // facility
    facilityName?: string;
    facilityIDs?: string[];
    facilityNames?: string[];
    radius?: number;
    notifyFacilityGuardians?: boolean;
    // country
    countryName?: string;
    countryISOs?: string[];
    countryNames?: string[];
    // incident
    incidentType: string;
    incidentFeedIDs?: string[];
    incidentTags?: string[];
    incidentTagLabels?: string[];
    excludedTags?: string[];
    excludedTagLabels?: string[];
    incidentTagMode?: string;
    incidentCategories?: string[];
    incidentSeverities?: string[];
    notifyTrackingTeam?: boolean;
    // recipients
    alertRecipients?: string[];
    notificationGroupIDs?: string[];
}

type SiteBasedAlertSelectorProps = {
    renderActions?: (alert: SiteBasedAlert) => React.ReactNode;
    onColumnClick?: (alert: SiteBasedAlert, columnIndex: number) => void;
}

export function SiteBasedAlertSelector({renderActions, onColumnClick}: SiteBasedAlertSelectorProps) {
    const {isSuperAdmin} = useAuth();

    const headers = [
        "Name",
        ...(isSuperAdmin ? ["Client"] : []),
        "Department",
        "Location Type",
        "Incident Type"
    ]

    const renderHeader = useCallback((props: HeaderComponentProps) => {
        // Column 1 is Client - make it filterable for super admins
        if (headers[props.columnIndex] == "Client" && isSuperAdmin) {
            return <ClientFilter {...props} />;
        }

        if (headers[props.columnIndex] == "Department" && (!isSuperAdmin || props.filterParams.clientID)) {
            return <DepartmentFilter {...props} clientID={props.filterParams.clientID as string | undefined} />;
        }

        // Column 2 is Location Type - make it filterable
        if (headers[props.columnIndex] == "Location Type") {
            return <LocationTypeFilter {...props} />;
        }
        return props.label;
    }, [isSuperAdmin, headers]);

    const getAlerts = useCallback(async (search: string, page: number, filterParams?: FilterQueryParams) => {
        const params: any = {
            search: search,
            page: page,
            limit: 10,
            ...filterParams,
        }

        const res = await apiV2.get<PaginatedResponse<any>>("site-based-alerts", {params})

        return convertV1ToV2(res.data, 10)
    }, [])

    const {exportToXlsx, isExporting} = useExportToXlsx<SiteBasedAlert>({
        getItems: getAlerts,
        filename: "site-based-alerts",
        exportPageSize: 500,
        columns: [
            {header: "id",                       width: 28, getValue: (a) => a.id},
            {header: "name",                     width: 30, getValue: (a) => a.name},
            {header: "locationType",             width: 15, getValue: (a) => a.locationType || ""},
            {header: "departmentID",             width: 28, getValue: (a) => a.departmentID || ""},
            {header: "countryCodes",             width: 20, getValue: (a) => a.countryISOs?.join(",") || ""},
            {header: "facilityIDs",              width: 35, getValue: (a) => a.facilityIDs?.join(",") || ""},
            {header: "radius",                   width: 10, getValue: (a) => a.radius ?? ""},
            {header: "notifyFacilityGuardians",  width: 24, getValue: (a) => a.notifyFacilityGuardians != null ? (a.notifyFacilityGuardians ? "yes" : "no") : ""},
            {header: "notifyTrackingTeam",       width: 20, getValue: (a) => a.notifyTrackingTeam != null ? (a.notifyTrackingTeam ? "yes" : "no") : ""},
            {header: "incidentFeedIDs",          width: 35, getValue: (a) => a.incidentFeedIDs?.join(",") || ""},
            {header: "incidentTags",             width: 35, getValue: (a) => a.incidentTags?.join(",") || ""},
            {header: "excludedTags",             width: 35, getValue: (a) => a.excludedTags?.join(",") || ""},
            {header: "incidentTagMode",          width: 14, getValue: (a) => a.incidentTagMode || ""},
            {header: "incidentTypes",            width: 35, getValue: (a) => a.incidentCategories?.join(",") || ""},
            {header: "incidentSeverities",       width: 30, getValue: (a) => a.incidentSeverities?.join(",") || ""},
            {header: "alertRecipients",          width: 35, getValue: (a) => a.alertRecipients?.join(",") || ""},
            {header: "alertNotificationGroups",  width: 35, getValue: (a) => a.notificationGroupIDs?.join(",") || ""},
        ]
    })

    const renderBottomActions = useCallback((search: string, filterParams: FilterQueryParams) => {
        return (
            <div className="flex justify-end">
                <div className="w-fit">
                    <ButtonV2
                        label={isExporting ? "Exporting..." : "Export"}
                        disabled={isExporting}
                        onClick={() => exportToXlsx(search, filterParams)}
                    />
                </div>
            </div>
        )
    }, [exportToXlsx, isExporting])

    const getColumns = useCallback((alert: SiteBasedAlert) => {
        let locationDisplay = "";
        if (alert.locationType === "country") {
            locationDisplay = alert.countryName
                ? `Country (${alert.countryName})`
                : "Country";
        } else if (alert.locationType === "facility") {
            const facilityPart = alert.facilityName
                ? `Facility (${alert.facilityName})`
                : "Facility";
            const radiusPart = alert.radius
                ? ` - ${(alert.radius / 1000).toFixed(1)}km`
                : "";
            locationDisplay = facilityPart + radiusPart;
        } else {
            locationDisplay = alert.locationType || "";
        }

        return [
            alert.name,
            ...(isSuperAdmin ? [alert.clientName] : []),
            alert.departmentID ? <DepartmentName departmentID={alert.departmentID} /> : <span className="text-light-grey">–</span>,
            locationDisplay,
            alert.incidentType || ""
        ]
    }, [])

    return <PaginatedSelector
        headers={headers}
        getItems={getAlerts}
        getColumns={getColumns}
        disableSelect={true}
        renderActions={renderActions}
        onColumnClick={onColumnClick}
        renderHeader={renderHeader}
        renderBottomActions={renderBottomActions}
        syncWithUrl={true}
    />
}
